import React, { useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { WorkToggle } from './WorkToggle';
import { TimeSelect } from './TimeSelect';
import { WeekSchedule, DAYS } from '../types/workingHours';
import { Button } from '@/components/ui/button';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import { validateDaySchedule } from '../utils/workGridMappers';

type Day = (typeof DAYS)[number];

export const DefaultScheduleTab = ({
    coachName,
    weekSchedule,
    onDayToggle,
    onSlotChange,
    onAddSlot,
    onRemoveSlot,
    onApplyToAll,
}: {
    coachName: string;
    weekSchedule: WeekSchedule;
    onDayToggle: (day: Day) => void;
    onSlotChange: (day: Day, slotIndex: number, field: 'startTime' | 'endTime', value: string) => void;
    onAddSlot: (day: Day) => void;
    onRemoveSlot: (day: Day, slotIndex: number) => void;
    /** 현재 강사의 근무 시간을 모든 강사에게 복사 */
    onApplyToAll: () => void;
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);

    const hasEnabledDay = DAYS.some((day) => weekSchedule[day].enabled);

    return (
        <Col className="h-full">
            <Row className="flex w-full justify-end">
                <div
                    className={`px-[16px] py-[8px] rounded-[10px] transition-colors ${
                        hasEnabledDay
                            ? 'bg-[#F2F4F6] cursor-pointer hover:bg-[#E8EAED]'
                            : 'bg-[#F2F4F6] opacity-40 cursor-not-allowed'
                    }`}
                    onClick={hasEnabledDay ? () => setConfirmOpen(true) : undefined}
                >
                    <RegularText fontSize={14} lineHeight={20} color="#4B5563">
                        모든 강사에게 적용
                    </RegularText>
                </div>
            </Row>

            {/* 컬럼 헤더 */}
            <Row className="mb-[16px] justify-between items-center">
                <Row>
                    <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="w-[60px] text-center">
                        요일
                    </DemiLightText>
                    <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="w-[80px] text-center">
                        근무 여부
                    </DemiLightText>
                    <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="ml-[20px]">
                        근무 시간
                    </DemiLightText>
                </Row>
            </Row>

            {/* 요일 리스트 — 슬롯이 많아지면 내부 스크롤 */}
            <div className="flex gap-[28px] flex-col overflow-y-auto pr-[8px] max-h-[560px]">
                {DAYS.map((day) => {
                    const schedule = weekSchedule[day];
                    const slots = schedule.timeSlots;
                    const errors = validateDaySchedule(schedule);
                    const errorBySlot = new Map<number, string>();
                    errors.forEach((e) => {
                        if (!errorBySlot.has(e.slotIndex)) errorBySlot.set(e.slotIndex, e.message);
                    });
                    return (
                        <Row key={day} className="items-start">
                            <RegularText
                                fontSize={18}
                                lineHeight={24}
                                color="#4B5563"
                                className="w-[60px] text-center pt-[14px]"
                            >
                                {day}
                            </RegularText>
                            <div className="w-[80px] flex justify-center pt-[14px]">
                                <WorkToggle value={schedule.enabled} onToggle={() => onDayToggle(day)} />
                            </div>
                            <Col className="ml-[20px] gap-[10px]">
                                {!schedule.enabled || slots.length === 0 ? (
                                    <Row className="items-center">
                                        <TimeSelect
                                            value=""
                                            onChange={() => {}}
                                            disabled
                                            placeholder="시작 시간"
                                        />
                                        <MediumText fontSize={20} lineHeight={24} color="#6B7280" className="mx-[12px]">
                                            ~
                                        </MediumText>
                                        <TimeSelect
                                            value=""
                                            onChange={() => {}}
                                            disabled
                                            placeholder="종료 시간"
                                        />
                                    </Row>
                                ) : (
                                    slots.map((slot, idx) => {
                                        const slotErr = errorBySlot.get(idx);
                                        const isEmptyErr = !slot.startTime || !slot.endTime;
                                        return (
                                            <Col key={idx} className="gap-[4px]">
                                                <Row className="items-center">
                                                    <TimeSelect
                                                        value={slot.startTime}
                                                        onChange={(val) => onSlotChange(day, idx, 'startTime', val)}
                                                        disabled={false}
                                                        placeholder={
                                                            <RegularText fontSize={17} color="#6B7280">
                                                                시작 시간
                                                            </RegularText>
                                                        }
                                                    />
                                                    <MediumText
                                                        fontSize={20}
                                                        lineHeight={24}
                                                        color="#6B7280"
                                                        className="mx-[12px]"
                                                    >
                                                        ~
                                                    </MediumText>
                                                    <TimeSelect
                                                        value={slot.endTime}
                                                        onChange={(val) => onSlotChange(day, idx, 'endTime', val)}
                                                        disabled={false}
                                                        allowDayEnd
                                                        placeholder={
                                                            <RegularText fontSize={17} color="#6B7280">
                                                                종료 시간
                                                            </RegularText>
                                                        }
                                                    />
                                                    <div
                                                        onClick={() => onRemoveSlot(day, idx)}
                                                        className="w-[28px] h-[28px] ml-[10px] rounded-full bg-[#F2F4F6] hover:bg-[#E6E8EA] flex items-center justify-center cursor-pointer transition-colors"
                                                        title="구간 삭제"
                                                    >
                                                        <SvgIcon
                                                            name="SvgX"
                                                            size={10}
                                                            stroke="#6B7280"
                                                            strokeWidth={2}
                                                        />
                                                    </div>
                                                </Row>
                                                {slotErr && !isEmptyErr && (
                                                    <RegularText fontSize={13} color="#D93043" className="ml-[4px]">
                                                        {slotErr}
                                                    </RegularText>
                                                )}
                                            </Col>
                                        );
                                    })
                                )}
                                {schedule.enabled && (
                                    <Row
                                        className="items-center cursor-pointer w-fit gap-[6px] pl-[2px]"
                                        onClick={() => onAddSlot(day)}
                                    >
                                        <SvgIcon name="SvgPlus" size={14} fill={colors.primary} />
                                        <RegularText fontSize={14} color={colors.primary}>
                                            시간 구간 추가
                                        </RegularText>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    );
                })}
            </div>

            {/* 모든 강사에게 적용 — 확인 모달 */}
            {confirmOpen && (
                <ApplyToAllConfirm
                    coachName={coachName}
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={() => {
                        onApplyToAll();
                        setConfirmOpen(false);
                    }}
                />
            )}
        </Col>
    );
};

const ApplyToAllConfirm = ({
    coachName,
    onCancel,
    onConfirm,
}: {
    coachName: string;
    onCancel: () => void;
    onConfirm: () => void;
}) => (
    <div
        className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
        onClick={(e) => {
            if (e.currentTarget === e.target) onCancel();
        }}
    >
        <div
            className="bg-white rounded-[20px] shadow-lg w-[460px] p-[24px]"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-[8px]">
                <MediumText fontSize={18} color="#1A1A1A">
                    모든 강사에게 적용할까요?
                </MediumText>
            </div>
            <div className="mb-[20px]">
                <RegularText fontSize={14} color="#6B7280">
                    {coachName ? `${coachName}` : '현재 강사'}의 요일별 근무 시간이 모든 강사에게 동일하게
                    복사됩니다. 다른 강사의 기존 설정은 덮어쓰기되어요.
                </RegularText>
            </div>
            <Row className="justify-end gap-[8px]">
                <Button
                    variant="ghost"
                    className="h-[40px] w-[100px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                    onClick={onCancel}
                >
                    <RegularText fontSize={14}>취소</RegularText>
                </Button>
                <Button
                    variant="default"
                    className="h-[40px] w-[100px] rounded-lg bg-[#3D73DD] hover:bg-[#3D73DD]/90 text-white"
                    onClick={onConfirm}
                >
                    <RegularText fontSize={14} color="#FFFFFF">
                        적용
                    </RegularText>
                </Button>
            </Row>
        </div>
    </div>
);
